import React, { useEffect, useState } from "react";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Edit,
  Shield,
  Award,
  Check,
  X,
  Camera,
  Briefcase,
} from "lucide-react";
import tokenService from "../../services/token-service";
import toast from "react-hot-toast";

const DEFAULT_SKILLS = [
  "General Maintenance",
  "Electrical Repairs",
  "Plumbing & HVAC",
  "Smart Home Setup",
];

const DEFAULT_CERTIFICATIONS = [
  { name: "Verified Service Professional", issuer: "Hazer Portal", year: "2024" },
];

export default function SharedProfile({ roleContext = "user" }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("general");

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    address: "",
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const authUser = tokenService.getUserDetails() || (
        localStorage.getItem("authUser")
          ? JSON.parse(localStorage.getItem("authUser"))
          : null
      );
      
      const token = tokenService.getToken();
      if (token && authUser?._id) {
        try {
          const res = await fetch(`${process.env.REACT_APP_API_URL}/users/${authUser._id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            const fetchedUser = data?.data?.user || data?.user || authUser;
            setUser(fetchedUser);
            setFormData({
              fullName: fetchedUser?.fullName || fetchedUser?.name || "",
              email: fetchedUser?.email || "",
              phoneNumber: fetchedUser?.phoneNumber || fetchedUser?.phone || "",
              address: fetchedUser?.address || "",
            });
            setLoading(false);
            return;
          }
        } catch (e) {
          console.warn("Could not fetch remote profile, falling back to cached:", e);
        }
      }

      // Fallback to cached authUser
      setUser(authUser);
      setFormData({
        fullName: authUser?.fullName || authUser?.name || "",
        email: authUser?.email || "",
        phoneNumber: authUser?.phoneNumber || authUser?.phone || "",
        address: authUser?.address || "",
      });
    } catch (error) {
      console.error("Error loading profile:", error);
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = tokenService.getToken();
      const authUser = user || tokenService.getUserDetails();

      if (token && authUser?._id) {
        try {
          const res = await fetch(`${process.env.REACT_APP_API_URL}/users/${authUser._id}`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(formData),
          });
          if (res.ok) {
            const data = await res.json();
            const updated = data?.data?.user || { ...authUser, ...formData };
            tokenService.setUserDetails(updated);
            setUser(updated);
            toast.success("Profile updated successfully!");
            setIsEditing(false);
            return;
          }
        } catch (err) {
          console.warn("API update failed, updating local state:", err);
        }
      }

      // Local update fallback
      const updatedUser = { ...authUser, ...formData };
      tokenService.setUserDetails(updatedUser);
      setUser(updatedUser);
      toast.success("Profile updated successfully");
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#EFA938] border-t-transparent"></div>
          <p className="text-gray-600 dark:text-gray-400 text-sm font-medium">Loading profile...</p>
        </div>
      </div>
    );
  }

  const displayName = user?.fullName || user?.name || "Account User";
  const userInitials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .substring(0, 2)
    .toUpperCase() || "U";

  const availableRolesRaw = user?.availableRoles || [];
  const rolesList = Array.isArray(availableRolesRaw) && availableRolesRaw.length > 0 && typeof availableRolesRaw[0] === "string"
    ? availableRolesRaw.map(r => r.toLowerCase())
    : (user?.roles || []).filter(r => r.isActive && r.roleId?.name).map(r => r.roleId.name.toLowerCase());
  
  const hasHandymanProfile = rolesList.includes("handyman") || user?.handyman?.isSubmitted || (user?.handymanApplicationStatus && user?.handymanApplicationStatus !== "none") || roleContext === "handyman" || user?.activeRole === "handyman";

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-12">
      {/* Top Title Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">
            Account Settings
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
            Manage your personal information and unified profile settings.
          </p>
        </div>

        {!isEditing && activeTab === "general" && (
          <button
            onClick={() => setIsEditing(true)}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#EFA938] hover:bg-[#d8962c] text-white font-semibold shadow-md shadow-[#EFA938]/20 transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
          >
            <Edit className="h-4 w-4" />
            Edit Profile
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-slate-700 mb-6">
        <button
          onClick={() => setActiveTab("general")}
          className={`px-6 py-3 font-semibold text-sm transition-colors border-b-2 ${
            activeTab === "general"
              ? "border-[#EFA938] text-[#EFA938]"
              : "border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          }`}
        >
          General Info
        </button>
        {hasHandymanProfile && (
          <button
            onClick={() => setActiveTab("handyman")}
            className={`px-6 py-3 font-semibold text-sm transition-colors border-b-2 ${
              activeTab === "handyman"
                ? "border-[#EFA938] text-[#EFA938]"
                : "border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            }`}
          >
            Handyman Details
          </button>
        )}
      </div>

      {/* Tab Content: General */}
      {activeTab === "general" && (
        <div className="rounded-3xl border border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-800 shadow-xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="p-6 md:p-8 flex flex-col md:flex-row items-center md:items-start gap-6">
            {/* Avatar */}
            <div className="relative group">
              <div className="h-28 w-28 rounded-full bg-[#EFA938] text-white flex items-center justify-center text-3xl font-bold border-4 border-white dark:border-slate-700 shadow-lg">
                {userInitials}
              </div>
              <button
                title="Change photo"
                className="absolute bottom-0 right-0 p-2 bg-gray-900/80 text-white rounded-full hover:bg-black transition-colors"
              >
                <Camera className="h-4 w-4" />
              </button>
            </div>

            {/* Core Info Summary */}
            <div className="flex-1 text-center md:text-left space-y-2">
              <div className="flex flex-col md:flex-row md:items-center gap-2">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{displayName}</h2>
                <span className="inline-flex items-center gap-1 self-center md:self-auto px-3 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider bg-[#FFF8EF] dark:bg-[#EFA938]/10 text-[#EFA938] border border-[#EFA938]/20">
                  <Shield className="h-3.5 w-3.5" />
                  {user?.role?.name || user?.activeRole || roleContext || "Customer"}
                </span>
              </div>

              <p className="text-gray-500 dark:text-gray-400 text-sm flex items-center justify-center md:justify-start gap-2">
                <Mail className="h-4 w-4 text-[#EFA938]" />
                {user?.email || "No email provided"}
              </p>

              <div className="pt-2 flex flex-wrap items-center justify-center md:justify-start gap-4 text-xs text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-1">
                  <Phone className="h-3.5 w-3.5 text-[#EFA938]" />
                  {user?.phoneNumber || user?.phone || "Not set"}
                </span>
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5 text-[#EFA938]" />
                  {user?.address || "Address not specified"}
                </span>
              </div>
            </div>
          </div>

          {/* Detailed Info Form / View */}
          <div className="border-t border-gray-100 dark:border-slate-700/80 p-6 md:p-8 bg-gray-50/50 dark:bg-slate-800/50">
            {isEditing ? (
              <form onSubmit={handleSave} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-2">
                      Full Name
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        value={formData.fullName}
                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                        required
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#EFA938]"
                        placeholder="Your full name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        disabled
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-100 dark:bg-slate-800 text-gray-500 cursor-not-allowed"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-2">
                      Phone Number
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        value={formData.phoneNumber}
                        onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#EFA938]"
                        placeholder="e.g. 03223456789"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-2">
                      Address / Location
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#EFA938]"
                        placeholder="City, Area or Street address"
                      />
                    </div>
                  </div>
                </div>

                {/* Edit Form Actions */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-slate-700">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-300 dark:border-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 text-sm font-semibold transition-colors"
                  >
                    <X className="h-4 w-4" />
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl bg-[#EFA938] hover:bg-[#d8962c] text-white text-sm font-semibold shadow-md transition-all"
                  >
                    <Check className="h-4 w-4" />
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </form>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-gray-100 dark:border-slate-800 flex items-start gap-4">
                  <div className="p-3 bg-[#FFF8EF] dark:bg-[#EFA938]/10 rounded-xl text-[#EFA938]">
                    <User className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Full Name</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white mt-0.5">{displayName}</p>
                  </div>
                </div>

                <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-gray-100 dark:border-slate-800 flex items-start gap-4">
                  <div className="p-3 bg-[#FFF8EF] dark:bg-[#EFA938]/10 rounded-xl text-[#EFA938]">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Email</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white mt-0.5">{user?.email || "—"}</p>
                  </div>
                </div>

                <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-gray-100 dark:border-slate-800 flex items-start gap-4">
                  <div className="p-3 bg-[#FFF8EF] dark:bg-[#EFA938]/10 rounded-xl text-[#EFA938]">
                    <Phone className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Phone Number</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white mt-0.5">
                      {user?.phoneNumber || user?.phone || "—"}
                    </p>
                  </div>
                </div>

                <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-gray-100 dark:border-slate-800 flex items-start gap-4">
                  <div className="p-3 bg-[#FFF8EF] dark:bg-[#EFA938]/10 rounded-xl text-[#EFA938]">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Address</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white mt-0.5">
                      {user?.address || "—"}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab Content: Handyman Details */}
      {activeTab === "handyman" && hasHandymanProfile && (
        <div className="rounded-3xl border border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-800 p-6 md:p-8 shadow-xl space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-[#FFF8EF] dark:bg-[#EFA938]/10 rounded-xl text-[#EFA938]">
              <Briefcase className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Professional Credentials & Skills</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Your verified handyman skills and certifications</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Verified Skills</p>
              <div className="flex flex-wrap gap-2">
                {(user?.handyman?.skills || DEFAULT_SKILLS).map((skill) => (
                  <span
                    key={skill}
                    className="inline-flex items-center rounded-full bg-[#FFF8EF] dark:bg-[#EFA938]/10 px-3.5 py-1 text-xs font-semibold text-[#EFA938] border border-[#EFA938]/20"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Certifications</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {DEFAULT_CERTIFICATIONS.map((cert, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 dark:bg-slate-900 rounded-xl border border-gray-100 dark:border-slate-800 flex items-center justify-between text-xs">
                    <div>
                      <p className="font-semibold text-gray-800 dark:text-gray-200">{cert.name}</p>
                      <p className="text-gray-400">Issued by {cert.issuer} ({cert.year})</p>
                    </div>
                    <Award className="h-4 w-4 text-[#EFA938]" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
